import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from './components/DashboardHeader';
import MetricsBar from './components/MetricsBar';
import ListingsTable from './components/ListingsTable';
import BulkUploadModal from './components/BulkUploadModal';
import QuickListModal from './components/QuickListModal';
import { useAuth } from '../../contexts/AuthContext';
import { listingsService } from '../../services/supabaseService';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile, signOut, loading: authLoading } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (searchParams?.get('tab') === 'bulk') setShowBulkUpload(true);
  }, [searchParams]);

  useEffect(() => {
    if (!authLoading && !user?.id) {
      navigate('/authentication?redirect=/agent-dashboard');
      return;
    }
    if (user?.id) {
      loadListings();
      // Real-time subscription
      const channel = listingsService?.subscribeToAgentListings(user?.id, (payload) => {
        if (payload?.eventType === 'INSERT') {
          setListings(prev => [formatListing(payload?.new), ...prev]);
        } else if (payload?.eventType === 'UPDATE') {
          setListings(prev => prev?.map(l => l?.id === payload?.new?.id ? { ...l, ...formatListing(payload?.new) } : l));
        } else if (payload?.eventType === 'DELETE') {
          setListings(prev => prev?.filter(l => l?.id !== payload?.old?.id));
        }
      });
      return () => {
        if (channel) import('../../lib/supabase')?.then(m => m?.supabase?.removeChannel(channel));
      };
    }
  }, [user?.id, authLoading]);

  const formatListing = (listing) => ({
    id: listing?.id,
    image: listing?.media?.[0]?.original_url || null,
    address: listing?.properties
      ? `${listing?.properties?.address}, ${listing?.properties?.suburb} ${listing?.properties?.state}`
      : listing?.address || 'Unknown Address',
    status: listing?.status || 'draft',
    created: listing?.created_at?.split('T')?.[0] || '',
    views: listing?.views || 0,
    leads: 0,
    completion: listing?.status === 'live' ? 100 : listing?.status === 'draft' ? 50 : 100,
    price: listing?.price_display || (listing?.price_or_rent ? `$${Number(listing?.price_or_rent)?.toLocaleString()}` : 'TBD'),
    raw: listing
  });

  const loadListings = async () => {
    setLoading(true);
    const { data } = await listingsService?.getByAgent(user?.id);
    setListings((data || [])?.map(formatListing));
    setLoading(false);
  };

  const metrics = {
    total: listings?.length,
    active: listings?.filter(l => l?.status === 'live')?.length,
    leads: listings?.reduce((sum, l) => sum + (l?.leads || 0), 0),
    views: listings?.reduce((sum, l) => sum + (l?.views || 0), 0),
  };

  const filteredListings = listings
    ?.filter(l => filter === 'all' || l?.status === filter)
    ?.filter(l => !searchQuery || l?.address?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
    ?.sort((a, b) => {
      let valA = a?.[sortBy === 'created_at' ? 'created' : sortBy];
      let valB = b?.[sortBy === 'created_at' ? 'created' : sortBy];
      if (sortBy === 'views' || sortBy === 'leads') { valA = Number(valA); valB = Number(valB); }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleStatusChange = async (id, newStatus) => {
    const { error } = await listingsService?.updateStatus(id, newStatus);
    if (!error) {
      setListings(prev => prev?.map(l => l?.id === id ? { ...l, status: newStatus } : l));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    await Promise.all(selectedIds?.map(id => listingsService?.updateStatus(id, newStatus)));
    setListings(prev => prev?.map(l => selectedIds?.includes(l?.id) ? { ...l, status: newStatus } : l));
    setSelectedIds([]);
  };

  const handleDelete = async (id) => {
    const { error } = await listingsService?.delete(id);
    if (!error) {
      setListings(prev => prev?.filter(l => l?.id !== id));
    }
  };

  const handleAddListing = async (newListing) => {
    if (!user?.id) return;
    const { data, error } = await listingsService?.create({
      propertyData: {
        address: newListing?.address || 'New Property',
        suburb: '',
        state: '',
        region: 'australia',
        propertyType: 'house'
      },
      listingData: {
        price: newListing?.price || '',
        saveAsDraft: newListing?.saveAsDraft
      },
      agentId: user?.id
    });
    if (!error && data) {
      await loadListings();
    }
    setShowQuickList(false);
  };

  const handleBulkImport = async (importedListings) => {
    if (!user?.id) return;
    for (const l of importedListings) {
      await listingsService?.create({
        propertyData: { address: l?.address || 'Imported Property', suburb: '', state: '', region: 'australia', propertyType: 'house' },
        listingData: { price: l?.price || '', saveAsDraft: true },
        agentId: user?.id
      });
    }
    await loadListings();
    setShowBulkUpload(false);
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0, 212, 255, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0, 212, 255, 0.02) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10">
        <DashboardHeader
          onAddListing={() => navigate('/create-listing')}
          onBulkUpload={() => setShowBulkUpload(true)}
          onQuickList={() => setShowQuickList(true)}
          onBack={() => navigate('/')}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          {(loading || authLoading) ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#00D4FF' }} />
            </div>
          ) : (
            <>
              <MetricsBar metrics={metrics} />
              <ListingsTable
                listings={filteredListings}
                filter={filter}
                setFilter={setFilter}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                onStatusChange={handleStatusChange}
                onBulkStatusChange={handleBulkStatusChange}
                onDelete={handleDelete}
                onEdit={(id) => navigate(`/create-listing?edit=${id}`)}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
            </>
          )}
        </div>
      </div>

      {showBulkUpload && (
        <BulkUploadModal onClose={() => setShowBulkUpload(false)} onImport={handleBulkImport} />
      )}
      {showQuickList && (
        <QuickListModal onClose={() => setShowQuickList(false)} onSave={handleAddListing} />
      )}
    </div>
  );
};

export default AgentDashboard;
